import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createNote, useProjectNotes } from "../../lib/queries";
import { useCockpit } from "../../lib/state";
import { renderMarkdown } from "../../lib/markdown";
import { relativeTime } from "../../lib/format";

interface NoteEditorProps {
  projectName: string;
}

export interface NoteEditorHandle {
  focus: () => void;
}

const NoteEditor = forwardRef<NoteEditorHandle, NoteEditorProps>(function NoteEditor(
  { projectName },
  ref,
) {
  const { data: notes = [] } = useProjectNotes(projectName);
  const openDrawer = useCockpit((s) => s.openDrawer);
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  useImperativeHandle(ref, () => ({
    focus: () => taRef.current?.focus(),
  }));

  const save = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    await createNote({ project: projectName, body: trimmed });
    setBody("");
    qc.invalidateQueries({ queryKey: ["project_notes"] });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
      e.preventDefault();
      save();
    }
  };

  const recent = notes.slice(0, 4);

  return (
    <div className="aero-glass mx-3 mt-3 flex flex-col" style={{ borderRadius: 22 }}>
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ borderBottom: "1px solid rgba(255,31,165,0.15)" }}
      >
        <div className="flex items-center gap-3">
          <span
            className="text-lg"
            style={{
              fontFamily: "Fraunces, serif",
              fontStyle: "italic",
              fontWeight: 700,
              color: "var(--ink)",
            }}
          >
            <span className="holo-text">Talk to Nigel</span>
          </span>
          <span
            className="font-eyebrow"
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "var(--plum)",
              opacity: 0.6,
            }}
          >
            agent reads &amp; acts
          </span>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={!body.trim()}
          className="glossy-pill glossy-pill--small disabled:opacity-40"
        >
          ★ Save
          <span className="kbd">⌘</span>
          <span className="kbd">S</span>
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 p-3">
        <div
          className="aero-glass aero-glass-tight"
          style={{ borderRadius: 14 }}
        >
          <textarea
            ref={taRef}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={`Spill it about ${projectName}.\n\n• "Lululemon scraper down — switch to Wayback fallback"\n• "Push 3 commits to main"\n• Use **bold**, [links](https://…), and \`code\`.`}
            className="min-h-[160px] w-full resize-y bg-transparent p-3 outline-none"
            style={{
              fontFamily: "Manrope, sans-serif",
              fontSize: 13,
              color: "var(--ink)",
              border: 0,
            }}
          />
        </div>
        <div
          className="aero-glass aero-glass-tight md-out p-3"
          style={{ borderRadius: 14 }}
          dangerouslySetInnerHTML={{
            __html:
              renderMarkdown(body) ||
              "<p style='color:var(--plum); opacity:.5; font-family:Fraunces, serif; font-style:italic'>preview</p>",
          }}
        />
      </div>
      {recent.length > 0 ? (
        <div style={{ borderTop: "1px solid rgba(255,31,165,0.15)" }}>
          <div className="flex items-center px-4 py-2">
            <span
              className="font-eyebrow"
              style={{
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: "var(--hot-pink)",
              }}
            >
              ✦ Recent Notes
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 px-3 pb-3">
            {recent.map((n) => {
              const interpreted = !!n.interpreted_by_agent_at;
              const actions = n.agent_actions ?? [];
              return (
                <button
                  type="button"
                  key={n.id}
                  onClick={() => openDrawer("note", n.id)}
                  className="aero-glass aero-glass-tight block w-full px-3 py-2 text-left transition-transform hover:-translate-y-0.5"
                  style={{ borderRadius: 12 }}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="font-eyebrow inline-flex items-center gap-1"
                      title={
                        interpreted
                          ? `interpreted ${relativeTime(n.interpreted_by_agent_at)}`
                          : "interpretation pending"
                      }
                      style={{
                        fontSize: 9,
                        fontWeight: 800,
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        color: interpreted ? "#7ec699" : "#d4a04c",
                      }}
                    >
                      {interpreted ? "✓ interpreted" : "⋯ pending"}
                    </span>
                    <span
                      className="font-eyebrow"
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: "0.1em",
                        color: "var(--plum)",
                        opacity: 0.55,
                      }}
                    >
                      {relativeTime(n.created_at)}
                    </span>
                  </div>
                  <span
                    className="line-clamp-2 mt-1 block"
                    style={{
                      fontFamily: "Fraunces, serif",
                      fontStyle: "italic",
                      fontSize: 12,
                      color: "var(--plum)",
                      opacity: 0.85,
                    }}
                  >
                    {n.body}
                  </span>
                  {actions.length > 0 ? (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {actions.map((a, i) => (
                        <span
                          key={i}
                          className="glossy-project-pill"
                          style={{
                            background: "linear-gradient(135deg, #ff64bf, #d4138a)",
                            fontSize: 8,
                            padding: "2px 7px",
                          }}
                        >
                          {a.kind}
                          {a.title ? `: ${a.title}` : ""}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
});

export default NoteEditor;
